import React, {useMemo} from 'react';
import {Box, Paper, Stack, Tooltip, Typography, useTheme} from '@mui/material';
import {Info} from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {packagingTokens as tk} from '@/theme/packagingTokens';

/**
 * BigNumber — a core-metric tile (Unitag design system §BigNumber): a
 * primary-colour accent bar on the left, a large value, and small supporting
 * text underneath. Mirrors the dashboard's `components/BigNumber` so the tiles
 * look identical across apps (8px radius, 1px border, soft shadow).
 *
 * The tile hugs its content (no forced height) so it never over-extends.
 * Pass `comparison` (previous-period value) to show a ▲/▼ % delta.
 */
type Props = {
    value: React.ReactNode;
    label: React.ReactNode;
    color?: string;
    helper?: string;
    comparison?: number | null;
    sx?: object;
};

export default function BigNumber({value, label, color, helper, comparison, sx}: Props) {
    const theme = useTheme();
    const accent = color ?? theme.palette.primary.main;

    const delta = useMemo(() => {
        if (comparison === null || comparison === undefined || typeof value !== 'number') return null;
        if (value === comparison) return {dir: 'flat' as const, pct: '0.0', prev: comparison};
        if (value === 0 || comparison === 0) {
            return {dir: value >= comparison ? 'up' as const : 'down' as const, pct: null, prev: comparison};
        }
        const pct = (((value - comparison) / comparison) * 100).toFixed(1);
        return {dir: value > comparison ? 'up' as const : 'down' as const, pct, prev: comparison};
    }, [value, comparison]);

    const deltaColor = !delta
        ? undefined
        : delta.dir === 'flat'
            ? theme.palette.text.disabled
            : delta.dir === 'up'
                ? tk.color.green
                : tk.color.red;

    return (
        <Paper
            elevation={0}
            sx={{
                position: 'relative',
                display: 'flex',
                gap: 1.5,
                border: `1px solid ${tk.color.border}`,
                borderRadius: '8px',
                boxShadow: tk.shadow.sm,
                p: 2,
                ...sx,
            }}
        >
            {helper && (
                <Box sx={{position: 'absolute', top: 8, right: 8}}>
                    <Tooltip title={helper} placement="top" arrow>
                        <Info fontSize="small" sx={{color: tk.color.textTertiary}}/>
                    </Tooltip>
                </Box>
            )}
            <Box sx={{width: 4, borderRadius: 2, backgroundColor: accent, alignSelf: 'stretch', flexShrink: 0}}/>
            <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 28, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', color: 'text.primary'}}>
                    {value}
                </Typography>
                <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5}}>
                    {label}
                </Typography>
                {delta && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{mt: 0.75}}>
                        {delta.dir !== 'flat' && (delta.dir === 'up'
                            ? <ArrowUpwardIcon sx={{fontSize: 16, color: deltaColor}}/>
                            : <ArrowDownwardIcon sx={{fontSize: 16, color: deltaColor}}/>)}
                        <Typography sx={{fontSize: 13, fontWeight: 600, color: deltaColor}}>
                            {delta.pct !== null ? `${delta.dir === 'up' ? '+' : ''}${delta.pct}%` : '—'}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: 'text.disabled'}}>
                            ({delta.prev})
                        </Typography>
                    </Stack>
                )}
            </Box>
        </Paper>
    );
}
